"use client";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useRouter } from "next/navigation";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../../../redux/slices/loginRegisterSlice";
import apiService from "../../../../utils/apiService";
import useWallet from "../../../../hooks/useWallet";
// import web3 from '@solana/web3.js';
import { PublicKey, LAMPORTS_PER_SOL, Connection } from "@solana/web3.js";
import {
  SOLANA_RPC_URL,
  resolveBrowserFallbackSolanaRpcUrl,
} from "../../../../config/rpc";
import {
  HEADER_BALANCE_REFRESH_EVENT,
  requestHeaderBalanceRefresh,
} from "../../../../utils/headerBalanceRefresh";

export default function useHeaderData() {
  const [showNetworkDropdown, setShowNetworkDropdown] = useState(false);
  const [showWalletDropdown, setShowWalletDropdown] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [autobotStatus, setAutobotStatus] = useState("active");
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showDisconnectWalletModal, setShowDisconnectWalletModal] =
    useState(false);
  const [userDataFromLocal, SetUserDataFromLocal] = useState({});
  const [solBalance, setSolBalance] = useState(0);
  const [isLoadingSolBalance, setIsLoadingSolBalance] = useState(false);
  const [solBalanceError, setSolBalanceError] = useState(null);
  const shouldPollBalance =
    (process.env.NEXT_PUBLIC_BALANCE_POLLING || "true").toLowerCase() ===
    "true";
  const balancePollMs = Math.max(
    8000,
    Number(process.env.NEXT_PUBLIC_BALANCE_POLL_MS || 12000),
  );
  // const SOL_MINT_ADDRESS = 'So11111111111111111111111111111111111111112';
  const primaryConnection = useMemo(
    () => new Connection(SOLANA_RPC_URL, "confirmed"),
    [],
  );
  const fallbackConnection = useMemo(() => {
    if (typeof window === "undefined") return null;
    const url = resolveBrowserFallbackSolanaRpcUrl(window.location.origin);
    return url ? new Connection(url, "confirmed") : null;
  }, []);
  const {
    isConnected: isWalletConnected,
    walletAddress,
    balance,
    connectedWallet,
    network: selectedNetwork,
    isConnecting,
    error: walletError,
    connect: connectWallet,
    handleWalletConnection: connectWalletGeneric,
    disconnect: disconnectWallet,
    formatAddress,
    copyAddress,
    detectWallets,
    isWalletInstalled,
    supportsSolana,
    clearError,
    walletProviders,
    getWalletStatus,
  } = useWallet();
  // Get authentication state from Redux
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  const [userProfile, setUserProfile] = useState({});
  const networks = [
    {
      id: "solana",
      name: "Solana",
      symbol: "SOL",
      color: "bg-purple-500",
      rpc: "Mainnet",
      chainId: "10",
    },
    {
      id: "ethereum",
      name: "Ethereum",
      symbol: "ETH",
      color: "bg-blue-500",
      rpc: "Mainnet",
      chainId: "1",
    },
    {
      id: "bsc",
      name: "BNB Chain",
      symbol: "BNB",
      color: "bg-yellow-500",
      rpc: "BSC",
      chainId: "56",
    },
    {
      id: "polygon",
      name: "Polygon",
      symbol: "MATIC",
      color: "bg-purple-500",
      rpc: "Polygon",
      chainId: "137",
    },
  ];

  
  // console.log(walletProviders)
  const router = useRouter();
  const dispatch = useDispatch();

  useEffect(() => {
    try {
      const userData = JSON.parse(localStorage.getItem("autobot_user"));
      SetUserDataFromLocal(userData || {});
    } catch (error) {
      console.error("Error parsing user data from localStorage:", error);
      SetUserDataFromLocal({});
    }
  }, []);

  // Update user profile when authentication state changes
  useEffect(() => {
    if (isAuthenticated && user) {
      setUserProfile({
        name: user.username || user.name || "User",
        email: user.email || "user@example.com",
        avatar: user.avatar || null,
        memberSince: user.createdAt
          ? new Date(user.createdAt).toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
            })
          : "January 2024",
        plan: user.plan || "Pro",
        totalBots: user.totalBots || 0,
        totalProfit: user.totalProfit || 0,
      });
    } else {
      // Reset to default when not authenticated
      setUserProfile({
        name: "Guest",
        email: "guest@example.com",
        avatar: null,
        memberSince: "N/A",
        plan: "Free",
        totalBots: 0,
        totalProfit: 0,
      });
      // Close profile dropdown when user is not authenticated
      setShowProfileDropdown(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    // Close dropdowns when clicking outside
    const handleClickOutside = (event) => {
      if (!event.target.closest(".dropdown-container")) {
        setShowNetworkDropdown(false);
        setShowWalletDropdown(false);
        setShowProfileDropdown(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    // Simulate autobot status monitoring (deterministic cycle to avoid hydration mismatch)
    const statuses = ["active", "scanning", "optimizing"];
    let index = 0;
    const interval = setInterval(() => {
      index = (index + 1) % statuses.length;
      setAutobotStatus(statuses[index]);
    }, 5000);

    return () => clearInterval(interval);
  }, []); // Empty dependency array to run only once

  const handleLogout = () => {
    setShowLogoutModal(false);
    setShowProfileDropdown(false);
    // Revoke tokens on server and clear local auth (redirects to /)
    apiService.logout();
  };

  const getAutobotStatusColor = () => {
    switch (autobotStatus) {
      case "active":
        return "bg-green-400";
      case "scanning":
        return "bg-gray-300";
      case "optimizing":
        return "bg-yellow-400";
      default:
        return "bg-gray-400";
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(value);
  };

  const getInitials = (name) => {
    if (!name || typeof name !== "string") {
      return "U"; // Default initial for undefined/null names
    }
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  const selectedNetworkData = networks.find((n) => n.id === selectedNetwork);


  // Function to fetch SOL balance from API.
  // silent: keep showing the current balance (no "Loading..." flicker) — used for
  // polls, focus, and manual refresh. Initial wallet connect still shows loading.
  const fetchSolBalance = useCallback(async (publicKey, { silent = false } = {}) => {
    if (!publicKey) {
      setSolBalance(0);
      setSolBalanceError(null);
      return;
    }

    if (!silent) setIsLoadingSolBalance(true);
    if (!silent) setSolBalanceError(null);
    const pk = new PublicKey(publicKey);
    const readSol = async (conn) => {
      const lamports = await conn.getBalance(pk);
      return lamports / LAMPORTS_PER_SOL;
    };

    try {
      const sol = await readSol(primaryConnection);
      setSolBalance(sol);
      setSolBalanceError(null);
    } catch (primaryErr) {
      if (fallbackConnection) {
        try {
          const sol = await readSol(fallbackConnection);
          setSolBalance(sol);
          setSolBalanceError(null);
          return;
        } catch {
          /* surface primary error below */
        }
      }
      console.error("Error fetching SOL balance:", primaryErr);
      if (!silent) setSolBalance(0);
      const msg =
        primaryErr?.message ||
        (typeof primaryErr === "string" ? primaryErr : "RPC unavailable");
      setSolBalanceError(msg.length > 80 ? `${msg.slice(0, 77)}…` : msg);
    } finally {
      if (!silent) setIsLoadingSolBalance(false);
    }
  }, [primaryConnection, fallbackConnection]);

  // Manual refresh: spin the icon only — UI keeps last balance (no "Loading..." text)
  const refreshSolBalance = useCallback(() => {
    if (isWalletConnected && walletAddress) {
      fetchSolBalance(walletAddress);
    }
  }, [isWalletConnected, walletAddress, fetchSolBalance]);

  // Enhanced copy address function that also refreshes balance
  const handleCopyAddress = useCallback(() => {
    copyAddress();
    // Refresh balance after copying address
    refreshSolBalance();
  }, [copyAddress, refreshSolBalance]);

  // Effect to fetch SOL balance when wallet address changes
  useEffect(() => {
    if (isWalletConnected && walletAddress) {
      fetchSolBalance(walletAddress);
    } else {
      setSolBalance(0);
    }
  }, [isWalletConnected, walletAddress, fetchSolBalance]);

  // Periodic refresh (default on). Set NEXT_PUBLIC_BALANCE_POLLING=false to disable.
  useEffect(() => {
    if (!shouldPollBalance || !isWalletConnected || !walletAddress) return;

    const interval = setInterval(() => {
      fetchSolBalance(walletAddress, { silent: true });
    }, balancePollMs);

    return () => clearInterval(interval);
  }, [shouldPollBalance, balancePollMs, isWalletConnected, walletAddress, fetchSolBalance]);

  // Immediate refresh when other parts of the app finish a wallet-affecting action (recharge, etc.).
  useEffect(() => {
    if (!isWalletConnected || !walletAddress) return;
    const onRefreshRequest = () =>
      fetchSolBalance(walletAddress, { silent: true });
    window.addEventListener(HEADER_BALANCE_REFRESH_EVENT, onRefreshRequest);
    return () => {
      window.removeEventListener(HEADER_BALANCE_REFRESH_EVENT, onRefreshRequest);
    };
  }, [isWalletConnected, walletAddress, fetchSolBalance]);

  // Keep header balance fresh when tab becomes active/visible.
  useEffect(() => {
    if (!isWalletConnected || !walletAddress) return;
    const onVisibility = () => {
      if (!document.hidden) fetchSolBalance(walletAddress, { silent: true });
    };
    const onFocus = () => fetchSolBalance(walletAddress, { silent: true });
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFocus);
    };
  }, [isWalletConnected, walletAddress, fetchSolBalance]);

  const handleWalletConnection = async (providerId) => {
    try {
      clearError();

      await connectWalletGeneric(providerId);

      setShowWalletDropdown(false);
      setShowMobileMenu(false);
      requestHeaderBalanceRefresh();
    } catch (error) {
      setTimeout(() => {
        if (!isWalletConnected) {
          let msg = "Failed to connect wallet. ";

          if (error.message.includes("User rejected"))
            msg += "User rejected connection.";
          else if (error.message.includes("not installed"))
            msg += "Wallet extension not installed.";
          else msg += error.message;

          alert(msg);
        }
      }, 300);
    }
  };

  return {
    // State
    showNetworkDropdown,
    setShowNetworkDropdown,
    showWalletDropdown,
    setShowWalletDropdown,
    showProfileDropdown,
    setShowProfileDropdown,
    showMobileMenu,
    setShowMobileMenu,
    autobotStatus,
    showLogoutModal,
    setShowLogoutModal,
    showDisconnectWalletModal,
    setShowDisconnectWalletModal,
    userDataFromLocal,
    solBalance,
    isLoadingSolBalance,
    solBalanceError,
    networks,
    walletProviders,

    // Wallet state
    isWalletConnected,
    walletAddress,
    balance,
    connectedWallet,
    selectedNetwork,
    isConnecting,
    walletError,
    userProfile,
    // Functions
    handleLogout,
    getAutobotStatusColor,
    formatCurrency,
    getInitials,
    selectedNetworkData,
    getWalletStatus,
    fetchSolBalance,
    refreshSolBalance,
    handleCopyAddress,
    handleWalletConnection,
    setUserProfile,
    // Wallet functions
    connectWallet,
    disconnectWallet,
    formatAddress,
    copyAddress,
    detectWallets,
    isWalletInstalled,
    supportsSolana,
    clearError,
  };
}